import React, { memo, useMemo } from 'react';
import dayjs from 'dayjs';

import icArrowDown from '../../../assets/imgs/icArrowDown.svg?react';
import useLocalizedTime from '../../../hooks/useLocalizedTime';
import { formatJpDate } from '../../../utils';
import CircleTicket from '../../circle-ticket/CircleTicket';
import DisplayNumber from '../../display-number/DisplayNumber';
import DisplayPeriodTime from '../../display-period/DisplayPeriodTime';
import { ATTENTION_COLOR, SECTION_TYPE } from '../../../utils/Constants';

import styles from './styles.module.css';
import { OrderTicket } from 'types';
import { Theme } from 'redux/reducers/rootReducer';

const DownOutlined = ({ styles }: { styles: React.CSSProperties | Record<string, any> }) => {
    return <div>{React.createElement(icArrowDown, { style: styles })}</div>;
};

interface ReservationTicketProps {
    orderTicket: OrderTicket;
    activeThemeIndex: number;
    colorFontCenter: string;
    circleSize: number;
}

const ReservationTicket = ({ orderTicket, activeThemeIndex, colorFontCenter, circleSize }: ReservationTicketProps) => {
    const { reservationInfo } = orderTicket?.orderUsageTickets?.[0] ?? {};
    const { formatTime } = useLocalizedTime();

    const { ticketDisplayReservationClass, ticketDisplayCenter, ticketDisplayOriginStop, ticketDisplayDestStop } =
        useMemo(() => {
            if (!reservationInfo) return {};

            return activeThemeIndex === 0
                ? {
                      ticketDisplayReservationClass: reservationInfo.ticketDisplayReservationClass1,
                      ticketDisplayCenter: reservationInfo.ticketDisplayCenter1,
                      ticketDisplayOriginStop: reservationInfo.originStopNameShort1,
                      ticketDisplayDestStop: reservationInfo.destStopNameShort1,
                  }
                : {
                      ticketDisplayReservationClass: reservationInfo.ticketDisplayReservationClass2,
                      ticketDisplayCenter: reservationInfo.ticketDisplayCenter2,
                      ticketDisplayOriginStop: reservationInfo.originStopNameShort2,
                      ticketDisplayDestStop: reservationInfo.destStopNameShort2,
                  };
        }, [activeThemeIndex, reservationInfo]);

    const isMultiSection = orderTicket?.sectionType === SECTION_TYPE.MULTI;

    const DisplayTime = () => {
        const shouldDisplayEndDate = orderTicket?.usingPeriodUnit === 'D' && (orderTicket?.usingPeriod ?? 0) > 1;
        const endTime = dayjs(reservationInfo?.reserveOn).add(orderTicket?.usingPeriod ?? 0, 'day');
        return (
            <>
                {shouldDisplayEndDate ? (
                    <div>
                        <div style={{ fontSize: circleSize * 0.06, lineHeight: 1 }}>
                            {formatJpDate(reservationInfo?.reserveOn, 'MM月DD日')}→
                        </div>
                        <div style={{ fontSize: isMultiSection ? circleSize * 0.06 : circleSize * 0.1 }}>
                            {formatJpDate(endTime, 'MM月DD日')}
                        </div>
                    </div>
                ) : (
                    <div style={{ fontSize: isMultiSection ? circleSize * 0.06 : circleSize * 0.1 }}>
                        {orderTicket?.usingPeriodUnit === 'D' || orderTicket?.isDisplayReserveOnTime === false
                            ? formatJpDate(reservationInfo?.reserveOn, 'MM月DD日')
                            : formatTime(reservationInfo?.reserveOn, 'LT')}
                    </div>
                )}
            </>
        );
    };

    return (
        <div
            className={styles.reservationInfo}
            style={{ color: colorFontCenter, height: isMultiSection ? '65%' : '45%' }}
        >
            <DisplayTime />

            {!isMultiSection && ticketDisplayReservationClass && (
                <div style={{ fontSize: circleSize * 0.06 }} className={styles.reservationClassName}>
                    {ticketDisplayReservationClass}
                </div>
            )}

            {ticketDisplayCenter && (
                <div style={{ fontSize: isMultiSection ? circleSize * 0.06 : circleSize * 0.125 }}>
                    {ticketDisplayCenter}
                </div>
            )}

            {isMultiSection && (
                <div>
                    <div style={{ fontSize: circleSize * 0.11 }}>{ticketDisplayOriginStop}</div>
                    <DownOutlined styles={{ width: circleSize * 0.09, fill: ATTENTION_COLOR }} />
                    <div style={{ fontSize: circleSize * 0.11 }}>{ticketDisplayDestStop}</div>
                </div>
            )}
        </div>
    );
};

interface MainContentProps {
    orderTicket: OrderTicket;
    currentTheme: Theme;
    circleSize: number;
    activeThemeIndex: number;
}

const MainContent = ({ orderTicket, currentTheme, circleSize, activeThemeIndex }: MainContentProps) => {
    const { colorCircleUpper, colorCircleLower, colorCircleRotation } = orderTicket ?? {};

    const { colorFontCenter } = currentTheme ?? {};

    return (
        <div className={styles.circleArea}>
            <div className={styles.circleView} style={{ width: circleSize, height: circleSize }}>
                <CircleTicket
                    upperHalfColor={colorCircleUpper ?? undefined}
                    lowerHalfColor={colorCircleLower ?? undefined}
                    rotationColor={colorCircleRotation ?? undefined}
                    circleSize={circleSize}
                />
                {orderTicket.isReserve ? (
                    <ReservationTicket
                        orderTicket={orderTicket}
                        activeThemeIndex={activeThemeIndex}
                        colorFontCenter={colorFontCenter}
                        circleSize={circleSize}
                    />
                ) : (
                    <DisplayPeriodTime orderTicket={orderTicket} textColor={colorFontCenter} circleSize={circleSize} />
                )}

                {!(orderTicket.sectionType === SECTION_TYPE.MULTI) && (
                    <DisplayNumber orderTicket={orderTicket} textColor={colorFontCenter} circleSize={circleSize} />
                )}
            </div>
        </div>
    );
};

export default memo(MainContent);
